// src/pages/Calendar.js
// ─────────────────────────────────────────────────────────────────────────────
// Refactored Calendar page with:
//   1. Search icon in header → toggles an inline search bar
//   2. react-calendar with blue/red dot markers (same as before)
//   3. "UPCOMING EVENTS" section with count badge below the calendar
//   4. EventCard list (sorted ASC by date, then time)
//   5. "No Events" empty state with calendar-X icon
//   6. EventModal opens when a card or a calendar day is clicked
//   7. Debounced search filters the event list by title, details, or date
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useContext, useCallback } from "react";
import { useSelector } from "react-redux";
import CalendarComponent from "react-calendar";
import { Spinner } from "react-bootstrap";
import { API_BASE_URL } from "../apiConfig";
import EventModal from "../components/EventModal";
import EventCard from "../components/EventCard";
import { AppContext } from "../context/appContext";
import "./Calendar.scss";

// ── Empty state ───────────────────────────────────────────────────────────────
function NoEventsState({ isSearch }) {
  return (
    <div className="no-events-state">
      <div className="no-events-icon-stack">
        {/* Calendar outline */}
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="#D1D5DB"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
          <line x1="16" y1="2" x2="16" y2="6" />
          <line x1="8" y1="2" x2="8" y2="6" />
          <line x1="3" y1="10" x2="21" y2="10" />
        </svg>
        {/* Red X badge */}
        <div className="no-events-x-badge" aria-hidden="true">
          <svg width="22" height="22" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="12" fill="#EF4444" />
            <path
              d="M15 9l-6 6M9 9l6 6"
              stroke="white"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </div>
      </div>
      <p className="no-events-title">No Events</p>
      <p className="no-events-subtitle">
        {isSearch
          ? "No events match your search."
          : "There are no upcoming events this month."}
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Calendar() {
  const user        = useSelector((state) => state.user);
  const { socket }  = useContext(AppContext);

  const [events,        setEvents]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalVisible,  setModalVisible]  = useState(false);
  const [today,         setToday]         = useState(new Date().toISOString().split("T")[0]);

  // ── Search ─────────────────────────────────────────────────────────────────
  const [searchActive, setSearchActive] = useState(false);
  const [searchQuery,  setSearchQuery]  = useState("");
  const [filteredQuery, setFilteredQuery] = useState(""); // debounced

  // 300 ms debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilteredQuery(searchQuery.trim().toLowerCase());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // ── Fetch events ───────────────────────────────────────────────────────────
  const fetchEvents = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/events?t=${Date.now()}`);
      const json     = await response.json();
      if (json.status === "ok") setEvents(json.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();

    // 1. 5-minute periodic sync
    const interval = setInterval(fetchEvents, 5 * 60 * 1000);

    // 2. Real-time socket updates
    const handleUpdate = () => fetchEvents();
    socket.on("new_notification", handleUpdate);
    socket.on("roomDeleted",      handleUpdate);

    // 3. Midnight rollover — update "today" so past/upcoming styling stays accurate
    const getMsUntilMidnight = () => {
      const next = new Date();
      next.setHours(24, 0, 0, 0);
      return next.getTime() - Date.now();
    };
    let midnightTimeout;
    const scheduleMidnight = () => {
      midnightTimeout = setTimeout(() => {
        setToday(new Date().toISOString().split("T")[0]);
        fetchEvents();
        scheduleMidnight();
      }, getMsUntilMidnight());
    };
    scheduleMidnight();

    return () => {
      clearInterval(interval);
      clearTimeout(midnightTimeout);
      socket.off("new_notification", handleUpdate);
      socket.off("roomDeleted",      handleUpdate);
    };
  }, [socket, fetchEvents]);

  // ── Guard: not logged in ───────────────────────────────────────────────────
  if (!user) {
    return (
      <div className="calendar-page-container d-flex justify-content-center pt-5">
        <h4>Please Login</h4>
      </div>
    );
  }

  // ── Calendar dot markers ───────────────────────────────────────────────────
  const markedDates = {};
  events.forEach((event) => {
    markedDates[event.date] = {
      marked:    true,
      dotColor:  event.date < today ? "#EF4444" : "#3B82F6",
      eventData: event,
    };
  });

  // Convert a JS Date → "YYYY-MM-DD" using local timezone
  const formatToYYYYMMDD = (date) => {
    const d     = new Date(date);
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day   = String(d.getDate()).padStart(2, "0");
    return `${d.getFullYear()}-${month}-${day}`;
  };

  const handleDayPress = (value) => {
    const dateString  = formatToYYYYMMDD(value);
    const eventOnDay  = markedDates[dateString]?.eventData;
    if (eventOnDay) {
      setSelectedEvent(eventOnDay);
      setModalVisible(true);
    }
  };

  const tileContent = ({ date, view }) => {
    if (view !== "month") return null;
    const dateString = formatToYYYYMMDD(date);
    const markInfo   = markedDates[dateString];
    if (!markInfo?.marked) return null;
    return (
      <div
        className="event-dot"
        style={{ backgroundColor: markInfo.dotColor }}
        aria-hidden="true"
      />
    );
  };

  // ── Upcoming events list (date >= today, sorted ASC) ──────────────────────
  const upcomingEvents = events
    .filter((e) => e.date >= today)
    .sort(
      (a, b) =>
        a.date.localeCompare(b.date) ||
        (a.time || "").localeCompare(b.time || "")
    );

  // Apply debounced search filter
  const displayedEvents = filteredQuery
    ? upcomingEvents.filter(
        (e) =>
          e.title.toLowerCase().includes(filteredQuery) ||
          (e.details || e.description || "").toLowerCase().includes(filteredQuery) ||
          e.date.includes(filteredQuery)
      )
    : upcomingEvents;

  const isSearching = searchActive && filteredQuery.length > 0;

  // ── Toggle search bar ──────────────────────────────────────────────────────
  const handleToggleSearch = () => {
    if (searchActive) {
      setSearchActive(false);
      setSearchQuery("");
    } else {
      setSearchActive(true);
    }
  };

  return (
    <div className="calendar-page-container">
      {/* ── Page header ───────────────────────────────────────────────────── */}
      <div className="cal-header">
        <h1 className="header-title">Event Calendar</h1>
        <button
          className={`cal-search-btn${searchActive ? " cal-search-btn--active" : ""}`}
          onClick={handleToggleSearch}
          aria-label={searchActive ? "Close search" : "Search events"}
          id="calendar-search-toggle"
        >
          {searchActive ? (
            /* X icon */
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          ) : (
            /* Search icon */
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          )}
        </button>
      </div>

      {/* ── Inline search bar (shown when search is active) ───────────────── */}
      <div className={`cal-search-bar-wrapper${searchActive ? " cal-search-bar-wrapper--visible" : ""}`}>
        <div className="cal-search-bar">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            id="calendar-search-input"
            type="text"
            className="cal-search-input"
            placeholder="Search by event name, date..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus={searchActive}
            aria-label="Search events"
          />
          {searchQuery && (
            <button
              className="cal-search-clear"
              onClick={() => setSearchQuery("")}
              aria-label="Clear search"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="text-center mt-5">
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <>
          {/* ── react-calendar ──────────────────────────────────────────────── */}
          <div className="calendar-card">
            <CalendarComponent
              onChange={handleDayPress}
              tileContent={tileContent}
              next2Label={null}
              prev2Label={null}
              minDetail="month"
            />
          </div>

          {/* ── Section header ───────────────────────────────────────────────── */}
          <div className="events-section-header">
            <span className="events-section-title">
              {isSearching ? "SEARCH RESULTS" : "UPCOMING EVENTS THIS MONTH"}
            </span>
            {displayedEvents.length > 0 && (
              <span className="events-count-badge" aria-label={`${displayedEvents.length} events`}>
                {displayedEvents.length}
              </span>
            )}
          </div>

          {/* ── Event cards / empty state ─────────────────────────────────────── */}
          {displayedEvents.length === 0 ? (
            <NoEventsState isSearch={isSearching} />
          ) : (
            <div className="events-list">
              {displayedEvents.map((event) => (
                <EventCard
                  key={event._id}
                  event={event}
                  onRefresh={fetchEvents}
                  onCardClick={() => {
                    setSelectedEvent(event);
                    setModalVisible(true);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Event detail modal ────────────────────────────────────────────── */}
      <EventModal
        visible={modalVisible}
        event={selectedEvent}
        onClose={() => setModalVisible(false)}
        onRefresh={() => {
          setModalVisible(false);
          fetchEvents();
        }}
      />
    </div>
  );
}

export default Calendar;
